/**
 * BasicDetailsStep Component
 *
 * Step 1: Collect assessment name, objective, timing, and voice type.
 * Includes optional advanced options for integration and tracking.
 */

import { useState } from 'react';
import { WizardState, WIZARD_LIMITS } from './types';

interface BasicDetailsStepProps {
  state: WizardState;
  onChange: (updates: Partial<WizardState>) => void;
  errors: string[];
}

export function BasicDetailsStep({ state, onChange, errors }: BasicDetailsStepProps) {
  const [showAdvanced, setShowAdvanced] = useState(
    !!(state.externalReferenceId || state.sourceSystem)
  );

  return (
    <div>
      <h2 style={{ marginBottom: 8 }}>Assessment Details</h2>
      <p className="text-muted" style={{ marginBottom: 24 }}>
        Start by providing the basic information about your assessment.
      </p>

      {errors.length > 0 && (
        <div className="error-box" style={{ marginBottom: 16 }}>
          {errors.map((err, i) => (
            <div key={i}>{err}</div>
          ))}
        </div>
      )}

      <div className="form-group">
        <label className="label">Assessment Name *</label>
        <input
          type="text"
          className="input"
          value={state.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="e.g., Customer Service Assessment"
          maxLength={WIZARD_LIMITS.NAME_MAX}
          style={{ maxWidth: 500 }}
        />
        <p className="text-muted text-small" style={{ marginTop: 4 }}>
          A descriptive name for this assessment (max {WIZARD_LIMITS.NAME_MAX} characters).
        </p>
      </div>

      <div className="form-group">
        <label className="label">Objective (optional)</label>
        <textarea
          className="input"
          value={state.objective}
          onChange={(e) => onChange({ objective: e.target.value })}
          placeholder="What should learners demonstrate? e.g., Handle an upset customer professionally"
          rows={4}
          maxLength={WIZARD_LIMITS.OBJECTIVE_MAX}
          style={{ maxWidth: 600, fontFamily: 'inherit' }}
        />
        <p className="text-muted text-small" style={{ marginTop: 4 }}>
          Describe the skill or behavior being assessed. AI will generate competencies from this.
        </p>
      </div>

      <div className="form-group">
        <label className="label">Assessment Timing *</label>
        <select
          className="input"
          value={state.assessmentTiming}
          onChange={(e) => onChange({ assessmentTiming: e.target.value as 'pre' | 'post' })}
          style={{ maxWidth: 400 }}
        >
          <option value="pre">Pre-training</option>
          <option value="post">Post-training</option>
        </select>
        <p className="text-muted text-small" style={{ marginTop: 4 }}>
          When this assessment is taken relative to training.
        </p>
      </div>

      <div className="form-group">
        <label className="label">Assessment Mode *</label>
        <select
          className="input"
          value={state.assessmentMode}
          onChange={(e) => onChange({ assessmentMode: e.target.value as 'voice' | 'quiz' })}
          style={{ maxWidth: 400 }}
        >
          <option value="voice">Voice Conversation</option>
          <option value="quiz">Online Quiz</option>
        </select>
        <p className="text-muted text-small" style={{ marginTop: 4 }}>
          {state.assessmentMode === 'voice'
            ? 'Real-time dialogue with an AI voice agent (3-10 minutes). Agent asks questions and adapts to responses.'
            : 'Multiple-choice quiz with AI-generated questions. Learners select answers from provided options.'}
        </p>
      </div>

      {/* Show score to learner */}
      <div className="form-group">
        <label className="label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={state.showScoreToLearner}
            onChange={(e) => onChange({ showScoreToLearner: e.target.checked })}
          />
          Show score to learner after completion
        </label>
        <p className="text-muted text-small" style={{ marginTop: 4 }}>
          If unchecked, learners see only a completion confirmation. Scores are always visible to trainers.
        </p>
      </div>

      {/* Advanced options toggle */}
      <div style={{ marginTop: 24 }}>
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            fontSize: 13,
            color: 'var(--sky-600)',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span style={{ fontSize: 10 }}>{showAdvanced ? '▼' : '▶'}</span>
          {showAdvanced ? 'Hide' : 'Show'} integration options
        </button>
      </div>

      {showAdvanced && (
        <div
          style={{
            marginTop: 16,
            padding: 16,
            background: 'var(--slate-50)',
            borderRadius: 8,
          }}
        >
          <div className="form-group">
            <label className="label">External Reference ID</label>
            <input
              type="text"
              className="input"
              value={state.externalReferenceId}
              onChange={(e) => onChange({ externalReferenceId: e.target.value })}
              placeholder="e.g., course-123 or session-456"
              style={{ maxWidth: 400 }}
            />
            <p className="text-muted text-small" style={{ marginTop: 4 }}>
              Optional identifier from your system. Helps prevent duplicate assessments on retry.
            </p>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="label">Source System</label>
            <input
              type="text"
              className="input"
              value={state.sourceSystem}
              onChange={(e) => onChange({ sourceSystem: e.target.value })}
              placeholder="e.g., LMS or TAP"
              style={{ maxWidth: 400 }}
            />
            <p className="text-muted text-small" style={{ marginTop: 4 }}>
              Optional identifier for the originating system.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
